/**
 * Secret Management API schemas
 */

import { z } from 'zod';
import {
    CredentialType,
    type ListSecretsForOrganizationQuery as ListSecretsForOrganizationQueryType,
    type CredentialUsageListItem,
    type CredentialUsageReference,
    type CredentialUsageType,
    type CreateSecretResponse,
    type Credentials,
    type CredentialUsagesResponse,
    type DisconnectCredentialUsagesRequest,
    type DisconnectCredentialUsagesResponse,
    type GetSecretResponse,
    type ListSecretsResponse,
    type SaveSecretResponse,
    type SecretListItem,
    type SecretWithCredentialsResponse,
    type SecretIdentityReference,
    type SecretOrganizationUuidReference,
    type SecretUuidReference,
    type SecretOrganizationReference,
} from '../secret.types';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';
import { NonCoreThirdPartySystemIdSchema } from './third-party-system.schemas';
import type { AuthorizableAction } from '@pillar-portal/database/dist/frontend';
import { ALL_AUTHORIZABLE_ACTIONS } from '@pillar-portal/database/dist/frontend';
import { SearchTermsSchema } from './omnisearch.schemas';

/**
 * Valor credentials public fields
 */
export const ValorCredentialsPublicSchema = z.object({
    domain: z.string().url(),
    epi: z.string(),
});

/**
 * Valor credentials secret fields
 */
export const ValorCredentialsSecretSchema = z.object({
    appid: z.string(),
    appkey: z.string(),
    channel: z.string(),
});

/**
 * Valor API credentials structure
 */
export const ValorCredentialsSchema = ValorCredentialsPublicSchema.merge(
    ValorCredentialsSecretSchema
);

export const DraftValorCredentialsSchema = z
    .object({
        domain: z.string().url().optional(),
        epi: z.string().min(1).optional(),
        appid: z.string().min(1).optional(),
        appkey: z.string().min(1).optional(),
        channel: z.string().min(1).optional(),
    })
    .refine((value) => Object.keys(value).length > 0, {
        message: 'At least one credential field is required',
    });

const CredentialTypeSchema = z.enum([
    CredentialType.PHYSICAL_TERMINAL,
    CredentialType.VIRTUAL_TERMINAL,
]);

const AuthorizableActionSchema: z.ZodType<AuthorizableAction> = z.enum(ALL_AUTHORIZABLE_ACTIONS);

const AuthorizableActionsQuerySchema = z
    .union([AuthorizableActionSchema, z.array(AuthorizableActionSchema)])
    .optional()
    .transform((value: AuthorizableAction | AuthorizableAction[] | undefined) => {
        if (value === undefined) {
            return undefined;
        }
        return Array.isArray(value) ? value : [value];
    });

const ListSecretsForOrganizationQuerySchemaBase: z.ZodType<ListSecretsForOrganizationQueryType> = z.object({
    actions: AuthorizableActionsQuerySchema,
    credential_type: CredentialTypeSchema.optional(),
    terms: SearchTermsSchema,
});
export const ListSecretsForOrganizationQuerySchema: z.ZodType<ListSecretsForOrganizationQueryType> =
    ListSecretsForOrganizationQuerySchemaBase;

/**
 * Organization secret (returned from API)
 */
const CredentialsSchemaBase = z.object({
    credential_id: z.number(),
    credential_uuid: z.string().uuid(),
    organization_id: z.number(),
    organization_uuid: z.string().uuid(),
    system_id: NonCoreThirdPartySystemIdSchema,
    account_id: z.string(),
    credential_type: CredentialTypeSchema,
    public_fields: z.record(z.string(), z.unknown()),
    masked_preview: z.string(),
    is_active: z.boolean(),
    applies_to_children: z.boolean(),
});
export const CredentialsSchema: z.ZodType<Credentials> = CredentialsSchemaBase;

/**
 * Organization secret with decrypted credentials
 */
export const CredentialsWithCredentialsSchema: z.ZodType<GetSecretResponse> =
    CredentialsSchemaBase.extend({
        credentials: DraftValorCredentialsSchema,
    });

/**
 * Create secret request
 */
export const CreateSecretRequestSchema = z.object({
    organization_uuid: z.string().uuid(),
    system_id: NonCoreThirdPartySystemIdSchema,
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    credentials: DraftValorCredentialsSchema,
    credential_label: z.string().optional(),
    applies_to_children: z.boolean().optional(),
});

/**
 * Update secret request
 */
export const UpdateSecretRequestSchema = z.object({
    account_id: z.string().min(1).optional(),
    credential_type: CredentialTypeSchema.optional(),
    credentials: DraftValorCredentialsSchema.optional(),
    credential_label: z.string().optional(),
    applies_to_children: z.boolean().optional(),
    is_active: z.boolean().optional(),
});

/**
 * Create secret response
 */
const CreateSecretResponseSchemaBase = z.object({
    credential_uuid: z.string().uuid(),
    organization_uuid: z.string().uuid(),
    system_id: NonCoreThirdPartySystemIdSchema,
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    public_fields: z.record(z.string(), z.unknown()),
    masked_preview: z.string(),
    is_active: z.boolean(),
    applies_to_children: z.boolean(),
});
export const CreateSecretResponseSchema: z.ZodType<CreateSecretResponse> =
    CreateSecretResponseSchemaBase;

export const SecretMetadataResponseSchema = CreateSecretResponseSchema;

export const SecretListItemSchema: z.ZodType<SecretListItem> = CreateSecretResponseSchemaBase;

/**
 * List secrets response
 */
export const ListSecretsResponseSchema: z.ZodType<ListSecretsResponse> = z.object({
    secrets: z.array(SecretListItemSchema),
});

/**
 * Get secret response (with decrypted credentials)
 */
export const SecretWithCredentialsResponseSchema: z.ZodType<SecretWithCredentialsResponse> =
    CreateSecretResponseSchemaBase.extend({
        credentials: DraftValorCredentialsSchema,
    });

export const GetSecretResponseSchema: z.ZodType<GetSecretResponse> =
    SecretWithCredentialsResponseSchema;

/**
 * Update secret response
 */
export const SaveSecretResponseSchema: z.ZodType<SaveSecretResponse> =
    CreateSecretResponseSchemaBase;

export const CredentialUsageTypeSchema: z.ZodType<CredentialUsageType> = z.union([
    z.literal('logical_terminal'),
    z.literal('terminal_device'),
]);

export const LogicalTerminalUsageReferenceSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'logical_terminal'>;
    logical_terminal_uuid: z.ZodString;
}> = z.object({
        usage_type: z.literal('logical_terminal'),
        logical_terminal_uuid: z.string().uuid(),
    });

export const TerminalDeviceUsageReferenceSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'terminal_device'>;
    terminal_device_uuid: z.ZodString;
}> = z.object({
        usage_type: z.literal('terminal_device'),
        terminal_device_uuid: z.string().uuid(),
    });

export const CredentialUsageReferenceSchema: z.ZodType<CredentialUsageReference> =
    z.discriminatedUnion('usage_type', [
        LogicalTerminalUsageReferenceSchema,
        TerminalDeviceUsageReferenceSchema,
    ]);

export const SecretIdentityReferenceSchema: z.ZodType<SecretIdentityReference> = z.object({
    credential_id: z.number(),
    credential_uuid: z.string().uuid(),
});

export const SecretUuidReferenceSchema: z.ZodType<SecretUuidReference> = z.object({
    credential_uuid: z.string().uuid(),
});

export const SecretOrganizationReferenceSchema: z.ZodType<SecretOrganizationReference> =
    z.object({
        organization_id: z.number(),
        organization_uuid: z.string().uuid(),
    });

export const SecretOrganizationUuidReferenceSchema: z.ZodType<SecretOrganizationUuidReference> =
    z.object({
        organization_uuid: z.string().uuid(),
    });

export const LogicalTerminalUsageListItemSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'logical_terminal'>;
    logical_terminal_uuid: z.ZodString;
    display_name: z.ZodString;
}> = LogicalTerminalUsageReferenceSchema.extend({
    display_name: z.string().min(1),
});

export const TerminalDeviceUsageListItemSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'terminal_device'>;
    terminal_device_uuid: z.ZodString;
    display_name: z.ZodString;
}> = TerminalDeviceUsageReferenceSchema.extend({
    display_name: z.string().min(1),
});

export const CredentialUsageListItemSchema: z.ZodType<CredentialUsageListItem> =
    z.discriminatedUnion('usage_type', [
        LogicalTerminalUsageListItemSchema,
        TerminalDeviceUsageListItemSchema,
    ]);

export const CredentialUsagesResponseSchema: z.ZodType<CredentialUsagesResponse> = z.object({
    usages: z.array(CredentialUsageListItemSchema),
});

export const DisconnectCredentialUsagesRequestSchema: z.ZodType<DisconnectCredentialUsagesRequest> = z.object({
    usages: z.array(CredentialUsageReferenceSchema).min(1),
});

export const DisconnectCredentialUsagesResponseSchema: z.ZodType<DisconnectCredentialUsagesResponse> = z.object({
    disconnected_count: z.number().int().min(0),
    remaining_usage_count: z.number().int().min(0),
    message: z.string().min(1),
});

/**
 * Common param schemas
 */
export const SystemIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const CredentialsIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const CredentialUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const AccountIdParamSchema: z.ZodType<string> = NonEmptyStringSchema;
